import "@/App.css";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  ChevronDown, 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MapPin,
  Shield,
  Leaf,
  Users,
  Award,
  Pickaxe,
  Truck,
  Compass,
  Settings,
  Gem,
  ArrowRight,
  CheckCircle2,
  Globe,
  TrendingUp,
  Target,
  Heart,
  Linkedin
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sikelela-mining/artifacts/2crias9e_logo.webp";

// Images - optimized with compression params
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_sikelela-mining/artifacts/bqwt6mxj_01.jpg",
  aboutCarousel: [
    "https://customer-assets.emergentagent.com/job_sikelela-mining/artifacts/vfpsb7ow_sikelela.png",
    "https://images.unsplash.com/photo-1644778055925-cf45809c2c17?crop=entropy&cs=srgb&fm=jpg&q=75&w=800",
    "https://images.pexels.com/photos/6328556/pexels-photo-6328556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  ],
  extraction: "https://images.unsplash.com/photo-1544531697-0f624508b0d4?crop=entropy&cs=srgb&fm=jpg&q=75&w=600",
  logistics: "https://customer-assets.emergentagent.com/job_sikelela-mining/artifacts/bqwt6mxj_01.jpg",
  exploration: "https://images.unsplash.com/photo-1625456825043-1ad812bc0d29?crop=entropy&cs=srgb&fm=jpg&q=75&w=600",
  silica: "https://images.unsplash.com/photo-1603062826804-97cb5bbb9110?crop=entropy&cs=srgb&fm=jpg&q=75&w=600",
  coal: "https://images.unsplash.com/photo-1632734825563-e180215f0c90?crop=entropy&cs=srgb&fm=jpg&q=75&w=600",
  chrome: "https://images.unsplash.com/photo-1746508765078-1ecdee3b0dac?crop=entropy&cs=srgb&fm=jpg&q=75&w=600",
  sustainability: "https://images.unsplash.com/photo-1681300658252-6151fcd2624d?crop=entropy&cs=srgb&fm=jpg&q=75&w=800"
};

// Navigation Component
function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Operations", href: "#operations" },
    { name: "Commodities", href: "#commodities" },
    { name: "Sustainability", href: "#sustainability" },
    { name: "Contact", href: "#contact" }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "bg-[#020617]/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Sikelela Mining" className="h-14 w-auto" />
            <div className="hidden sm:block">
              <span className="text-[#d4af37] font-extrabold text-2xl lg:text-3xl tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                SIKELELA
              </span>
              <span className="block text-white/80 text-xs tracking-[0.25em] uppercase -mt-1">Mining Group</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm font-medium tracking-wide uppercase"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
            <button 
              onClick={() => scrollToSection("#contact")}
              className="btn-primary text-sm"
              data-testid="nav-cta-button"
            >
              Get In Touch
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#0f172a] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left text-white/80 hover:text-[#d4af37] py-2 uppercase tracking-wide text-sm"
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section id="home" className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={IMAGES.hero} 
          alt="Mining Operations - Komatsu Truck" 
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/90 via-[#020617]/70 to-transparent" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-0">
        <div className="max-w-3xl">
          <div className="opacity-0 animate-fade-in-up">
            <span className="inline-block text-[#d4af37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              Zimbabwe's Premier Mining Company
            </span>
          </div>
          
          <h1 
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white uppercase leading-[0.9] mb-6 opacity-0 animate-fade-in-up animation-delay-200"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Driving Sustainable<br />
            <span className="text-[#d4af37]">Mining Excellence</span>
          </h1>
          
          <p className="text-lg text-white/70 mb-10 max-w-xl leading-relaxed opacity-0 animate-fade-in-up animation-delay-400">
            At Sikelela Mining Group, we combine industry expertise with responsible practices 
            to unlock Africa's mineral wealth while creating lasting value for communities and investors.
          </p>

          <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up animation-delay-600">
            <a 
              href="#operations" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="hero-cta-operations"
            >
              Explore Our Operations
              <ArrowRight size={18} />
            </a>
            <a 
              href="#contact" 
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="hero-cta-contact"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-[#d4af37]" size={32} />
      </div>
    </section>
  );
}

// Stats Section
function StatsSection() {
  const stats = [
    { number: "15+", label: "Years Experience" },
    { number: "50+", label: "Mining Projects" },
    { number: "500+", label: "Employees" },
    { number: "3", label: "Key Minerals" }
  ];

  return (
    <section className="bg-[#0f172a] py-16 border-y border-white/5" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center" data-testid={`stat-${index}`}>
              <div className="stat-number">{stat.number}</div>
              <div className="text-white/60 uppercase tracking-wider text-sm mt-2">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselImages = IMAGES.aboutCarousel;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 5000); // Change image every 5 seconds
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const values = [
    { icon: Shield, title: "Safety First", desc: "Zero compromise on workplace safety standards" },
    { icon: Leaf, title: "Sustainability", desc: "Environmentally responsible mining practices" },
    { icon: Award, title: "Integrity", desc: "Ethical business conduct in all operations" },
    { icon: TrendingUp, title: "Innovation", desc: "Embracing technology for efficiency" }
  ];

  return (
    <section id="about" className="py-24 lg:py-32 bg-[#020617]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Carousel Image Side */}
          <div className="relative h-[500px] overflow-hidden">
            {carouselImages.map((image, index) => (
              <img 
                key={index}
                src={image}
                alt={index === 0 ? "Sikelela Mining Vehicle" : index === 1 ? "African Mining Workers" : "Underground Mine Shaft"}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
                loading={index === 0 ? "eager" : "lazy"}
              />
            ))}
            {/* Carousel Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentImageIndex ? 'bg-[#d4af37] w-8' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#d4af37] p-8 hidden lg:block z-20">
              <div className="stat-number text-[#0f172a]">15+</div>
              <div className="text-[#0f172a]/80 uppercase tracking-wider text-sm">Years of Excellence</div>
            </div>
          </div>

          {/* Content Side */}
          <div>
            <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">About Us</span>
            <h2 
              className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Pioneering Mining Excellence in Zimbabwe
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              Sikelela Mining Group is a Zimbabwean mining company at the forefront of mining and mineral 
              exploration. We conduct our business with skill, respect, confidence, and integrity, sharing 
              a common ideal of professionalism that positions us at the leading edge of investment in 
              mining and mineral exploration in Zimbabwe.
            </p>
            <p className="text-white/70 leading-relaxed mb-8">
              With a substantial portfolio of available mines, we provide strategic access to foreign 
              investors and partners seeking opportunities in Africa's rich mineral landscape. Our commitment 
              to sustainable practices ensures we create lasting value for all stakeholders.
            </p>

            {/* Vision & Mission */}
            <div className="grid sm:grid-cols-2 gap-6 mb-10">
              <div className="bg-white/5 border border-white/10 p-6">
                <Target className="text-[#d4af37] mb-4" size={28} />
                <h3 className="text-white font-bold uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Our Vision
                </h3>
                <p className="text-white/60 text-sm">
                  To be Africa's most trusted and sustainable mining partner, setting industry benchmarks 
                  for operational excellence.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 p-6">
                <Globe className="text-[#d4af37] mb-4" size={28} />
                <h3 className="text-white font-bold uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Our Mission
                </h3>
                <p className="text-white/60 text-sm">
                  To unlock mineral wealth responsibly while creating prosperity for communities, 
                  partners, and shareholders.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <h3 className="text-white font-bold uppercase tracking-wide mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Core Values
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {values.map((value, index) => (
                <div key={index} className="flex items-start gap-3" data-testid={`value-${index}`}>
                  <value.icon className="text-[#d4af37] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <span className="text-white font-medium text-sm">{value.title}</span>
                    <p className="text-white/50 text-xs mt-1">{value.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Operations Section
function OperationsSection() {
  const services = [
    {
      icon: Pickaxe,
      title: "Mineral Extraction",
      desc: "Full-scale mining operations utilizing advanced techniques for optimal resource recovery across diverse mineral deposits.",
      image: IMAGES.extraction
    },
    {
      icon: Compass,
      title: "Exploration Services",
      desc: "Comprehensive geological surveys and resource assessment to identify and evaluate mineral potential.",
      image: IMAGES.exploration
    },
    {
      icon: Truck,
      title: "Logistics & Supply Chain",
      desc: "End-to-end transportation and supply chain management ensuring efficient movement of materials and minerals.",
      image: IMAGES.logistics
    },
    {
      icon: Settings,
      title: "Contract Mining",
      desc: "Professional mining services with flexible engagement models tailored to project requirements.",
      image: IMAGES.extraction
    },
    {
      icon: Gem,
      title: "Equipment & Infrastructure",
      desc: "State-of-the-art mining equipment and infrastructure development for operational excellence.",
      image: IMAGES.logistics
    },
    {
      icon: Shield,
      title: "Environmental Services",
      desc: "Environmental monitoring, compliance auditing, and mine closure planning with rehabilitation expertise.",
      image: IMAGES.sustainability
    }
  ];

  return (
    <section id="operations" className="py-24 lg:py-32 bg-[#0f172a]" data-testid="operations-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">Our Services</span>
          <h2 
            className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent-center"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Operations & Services
          </h2>
          <p className="text-white/70">
            Comprehensive mining solutions delivered with expertise, efficiency, and environmental responsibility.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              className="service-card group bg-[#020617] border border-white/5 p-8 hover:border-[#d4af37]/30 transition-all duration-500"
              data-testid={`service-${index}`}
            >
              <service.icon className="text-[#d4af37] mb-6 group-hover:scale-110 transition-transform duration-300" size={40} />
              <h3 
                className="text-white text-xl font-bold uppercase tracking-wide mb-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {service.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {service.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Commodities Section
function CommoditiesSection() {
  const commodities = [
    {
      name: "Silica / Quartz",
      desc: "High-purity silica and quartz extraction for industrial applications, glass manufacturing, and electronics industries.",
      image: IMAGES.silica,
      specs: ["99.5% Purity", "Industrial Grade", "Export Quality"]
    },
    {
      name: "Chrome",
      desc: "Premium chromite ore mining for metallurgical and chemical industries, supporting steel production globally.",
      image: IMAGES.chrome,
      specs: ["High Cr2O3 Content", "Lumpy & Concentrate", "Consistent Supply"]
    },
    {
      name: "Coal",
      desc: "Thermal and metallurgical coal extraction supporting energy generation and industrial processes.",
      image: IMAGES.coal,
      specs: ["High Calorific Value", "Low Ash Content", "Reliable Delivery"]
    }
  ];

  return (
    <section id="commodities" className="py-24 lg:py-32 bg-[#020617]" data-testid="commodities-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">Our Portfolio</span>
          <h2 
            className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent-center"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Commodities We Mine
          </h2>
          <p className="text-white/70">
            Sikelela Mining Group specializes in the extraction and supply of high-quality minerals 
            to global markets.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {commodities.map((commodity, index) => (
            <div 
              key={index}
              className="commodity-card relative h-[500px]"
              data-testid={`commodity-${index}`}
            >
              <img 
                src={commodity.image}
                alt={commodity.name}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="overlay" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <h3 
                  className="text-white text-2xl lg:text-3xl font-bold uppercase mb-4"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {commodity.name}
                </h3>
                <p className="text-white/70 text-sm mb-6 leading-relaxed">
                  {commodity.desc}
                </p>
                <div className="flex flex-wrap gap-2">
                  {commodity.specs.map((spec, i) => (
                    <span 
                      key={i}
                      className="text-xs bg-[#d4af37]/20 text-[#d4af37] px-3 py-1 border border-[#d4af37]/30"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Why Choose Us Section
function WhyChooseUsSection() {
  const reasons = [
    {
      icon: CheckCircle2,
      title: "Proven Track Record",
      desc: "Over 15 years of successful mining operations and project delivery across Zimbabwe."
    },
    {
      icon: Shield,
      title: "Regulatory Compliance",
      desc: "Full compliance with all mining regulations and environmental standards."
    },
    {
      icon: Users,
      title: "Expert Team",
      desc: "Skilled professionals with deep expertise in geology, engineering, and mining operations."
    },
    {
      icon: Globe,
      title: "Strategic Partnerships",
      desc: "Strong relationships with international investors and industry leaders."
    },
    {
      icon: Leaf,
      title: "Sustainable Practices",
      desc: "Commitment to environmental stewardship and community development."
    },
    {
      icon: TrendingUp,
      title: "Investment Ready",
      desc: "Comprehensive portfolio of mining assets available for strategic investment."
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0f172a]" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">Why Partner With Us</span>
            <h2 
              className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Why Sikelela Mining?
            </h2>
            <p className="text-white/70 leading-relaxed mb-10">
              When you partner with Sikelela Mining Group, you gain access to Zimbabwe's mineral wealth 
              through a trusted, experienced, and responsible mining partner. We deliver results while 
              maintaining the highest standards of safety, compliance, and sustainability.
            </p>
            <a 
              href="#contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="why-choose-cta"
            >
              Start a Conversation
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 p-6 hover:border-[#d4af37]/30 transition-colors duration-300"
                data-testid={`reason-${index}`}
              >
                <reason.icon className="text-[#d4af37] mb-4" size={28} />
                <h3 className="text-white font-bold uppercase tracking-wide mb-2 text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {reason.title}
                </h3>
                <p className="text-white/60 text-sm">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Sustainability Section
function SustainabilitySection() {
  const pillars = [
    {
      icon: Leaf,
      title: "Environmental Responsibility",
      desc: "We implement rigorous environmental management systems, minimize our footprint, and rehabilitate mined areas to restore natural ecosystems."
    },
    {
      icon: Users,
      title: "Community Development",
      desc: "We invest in local communities through employment, education, healthcare initiatives, and infrastructure development programs."
    },
    {
      icon: Shield,
      title: "Safety Standards",
      desc: "Zero-harm workplace culture with comprehensive safety training, equipment, and protocols exceeding industry standards."
    },
    {
      icon: Award,
      title: "Governance & Compliance",
      desc: "Transparent business practices, ethical conduct, and full compliance with all regulatory and legal requirements."
    }
  ];

  return (
    <section id="sustainability" className="py-24 lg:py-32 bg-[#020617] relative" data-testid="sustainability-section">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src={IMAGES.sustainability}
          alt="Sustainability"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 bg-[#020617]/90" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">ESG Commitment</span>
          <h2 
            className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent-center"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Sustainability & Responsibility
          </h2>
          <p className="text-white/70">
            At Sikelela Mining Group, sustainability isn't just a practice—it's our responsibility. 
            We are committed to creating lasting positive impact for the environment and communities.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {pillars.map((pillar, index) => (
            <div 
              key={index}
              className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 flex gap-6 hover:border-[#d4af37]/30 transition-colors duration-300"
              data-testid={`pillar-${index}`}
            >
              <pillar.icon className="text-[#d4af37] flex-shrink-0" size={40} />
              <div>
                <h3 
                  className="text-white text-xl font-bold uppercase tracking-wide mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {pillar.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{pillar.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Leadership Section
function LeadershipSection() {
  const team = [
    { name: "Coming Soon", role: "Chief Executive Officer", image: null },
    { name: "Coming Soon", role: "Chief Operations Officer", image: null },
    { name: "Coming Soon", role: "Chief Financial Officer", image: null },
    { name: "Coming Soon", role: "Head of Geology", image: null }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0f172a]" data-testid="leadership-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">Our Team</span>
          <h2 
            className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent-center"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Leadership Team
          </h2>
          <p className="text-white/70">
            Experienced professionals driving Sikelela Mining Group towards excellence.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, index) => (
            <div 
              key={index}
              className="bg-[#020617] border border-white/10 p-6 text-center group hover:border-[#d4af37]/30 transition-colors duration-300"
              data-testid={`team-member-${index}`}
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center">
                <Users className="text-white/40" size={40} />
              </div>
              <h3 
                className="text-white font-bold uppercase tracking-wide mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {member.name}
              </h3>
              <p className="text-[#d4af37] text-sm">{member.role}</p>
              <div className="flex justify-center gap-3 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <Linkedin className="text-white/40 hover:text-[#d4af37] cursor-pointer" size={20} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    honeypot: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      toast.success(response.data.message);
      setFormData({ name: "", email: "", phone: "", message: "", honeypot: "" });
    } catch (error) {
      setSubmitStatus("error");
      toast.error(error.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: "Email", value: "info@sikelelamining.com", href: "mailto:info@sikelelamining.com" },
    { icon: Phone, label: "Phone", value: "+263 XXX XXX XXX", href: "tel:+263XXXXXXXX" },
    { icon: MapPin, label: "Location", value: "Harare, Zimbabwe", href: null }
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#020617]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <span className="text-[#d4af37] text-sm tracking-[0.3em] uppercase">Get In Touch</span>
            <h2 
              className="text-4xl lg:text-5xl font-extrabold text-white uppercase mt-4 mb-6 line-accent"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Contact Us
            </h2>
            <p className="text-white/70 mb-8">
              Ready to explore partnership opportunities? Get in touch with our team to discuss 
              how we can work together.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Honeypot field - hidden */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/80 text-sm mb-2 uppercase tracking-wide">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="form-input w-full px-4 py-3"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2 uppercase tracking-wide">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input w-full px-4 py-3"
                    placeholder="Your email"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white/80 text-sm mb-2 uppercase tracking-wide">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input w-full px-4 py-3"
                  placeholder="Your phone number"
                  data-testid="contact-phone-input"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm mb-2 uppercase tracking-wide">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  minLength={10}
                  rows={5}
                  className="form-input w-full px-4 py-3 resize-none"
                  placeholder="How can we help you?"
                  data-testid="contact-message-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              {submitStatus === "success" && (
                <div className="flex items-center gap-2 text-green-400" data-testid="contact-success-message">
                  <CheckCircle2 size={20} />
                  <span>Message sent successfully!</span>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-white/5 border border-white/10 p-8">
              <h3 
                className="text-white text-xl font-bold uppercase tracking-wide mb-6"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Contact Information
              </h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                    <info.icon className="text-[#d4af37] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <span className="text-white/60 text-sm uppercase tracking-wide">{info.label}</span>
                      {info.href ? (
                        <a href={info.href} className="block text-white hover:text-[#d4af37] transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <span className="block text-white">{info.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="h-[300px] bg-white/5 border border-white/10 overflow-hidden" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121059.04063379655!2d30.98264283476562!3d-17.82917139999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a4e1b9f7a2b7%3A0x5b2d9a9d7a5a7d5d!2sHarare%2C%20Zimbabwe!5e0!3m2!1sen!2s!4v1640000000000!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sikelela Mining Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section className="py-20 bg-[#d4af37]" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <h2 
          className="text-3xl lg:text-4xl font-extrabold text-[#0f172a] uppercase mb-4"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Ready to Partner With Us?
        </h2>
        <p className="text-[#0f172a]/80 mb-8 max-w-2xl mx-auto">
          Discover investment opportunities in Zimbabwe's mineral wealth with a trusted partner 
          committed to excellence and sustainability.
        </p>
        <a 
          href="#contact"
          className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-8 py-4 uppercase font-bold tracking-widest hover:bg-[#1e293b] transition-colors"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="cta-button"
        >
          Start a Conversation
          <ArrowRight size={18} />
        </a>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0f172a] py-16 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Sikelela Mining" className="h-14 w-auto" />
              <div>
                <span className="text-[#d4af37] font-extrabold text-2xl tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  SIKELELA
                </span>
                <span className="block text-white/80 text-xs tracking-[0.25em] uppercase -mt-1">Mining Group</span>
              </div>
            </div>
            <p className="text-white/60 max-w-md mb-6">
              A leading Zimbabwean mining company committed to sustainable mineral extraction, 
              responsible practices, and creating lasting value for communities and investors.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wide mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {["About", "Operations", "Commodities", "Sustainability", "Contact"].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase()}`}
                    className="text-white/60 hover:text-[#d4af37] transition-colors text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wide mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#d4af37]" />
                info@sikelelamining.com
              </li>
              <li className="flex items-center gap-2">
                <MapPin size={16} className="text-[#d4af37]" />
                Harare, Zimbabwe
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} Sikelela Mining Group. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-white/40 text-sm">
            <a href="#" className="hover:text-[#d4af37] transition-colors">Privacy Policy</a>
            <span>|</span>
            <a href="#" className="hover:text-[#d4af37] transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-[#020617]">
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            background: '#0f172a',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            color: '#f8fafc'
          }
        }}
      />
      <Navigation />
      <main>
        <HeroSection />
        <StatsSection />
        <AboutSection />
        <OperationsSection />
        <CommoditiesSection />
        <WhyChooseUsSection />
        <SustainabilitySection />
        <LeadershipSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
